
import { Component, Vue, Watch } from 'vue-property-decorator';
import moment from 'moment';

import { DATA } from '@/config/data';
import { APP_CONFIG } from '@/config/app-config';

import orderService from '@/services/order-service';
import commonService from '@/services/common-services';
import authService from '@/services/auth-services';

import { Order } from '@/models/order';
import { Product } from '@/models/product';
import { ChangedHistoryModel } from '@/models/changed-history';
import { PaymentHistoryModel } from '@/models/payment-history';
import { OrderComplainModel } from '@/models/order-complain';

import ListProduct from './components/ListProduct.vue';
import NewProduct from './components/NewProduct.vue';
import AmountDetailOrder from './components/AmountDetailOrder.vue';
import CustomerInfo from './components/CustomerInfo.vue';
import ChangedHistory from './components/ChangedHistory.vue';
import PaymentHistory from './components/PaymentHistory.vue';
import OrderComplain from './components/OrderComplain.vue';
import OrderDetailFooter from './components/OrderDetailFooter.vue';
import BillOfLading from './components/BillOfLading.vue';
import ChatBox from '@/shared/components/ChatBox.vue';

@Component({
    components: {
        'list-product': ListProduct,
        'new-product': NewProduct,
        'amount-detail': AmountDetailOrder,
        'customer-info': CustomerInfo,
        'chat-box': ChatBox,
        'change-hist': ChangedHistory,
        'payment-hist': PaymentHistory,
        'order-complain': OrderComplain,
        'order-detail-footer': OrderDetailFooter,
        'bill-of-lading': BillOfLading
    }
})
export default class DetailOrder extends Vue {
    private order: Order = new Order();
    private customerInfo: any = {};

    get orderId() {
        return this.$route.params.orderId;
    }

    get customerLevels() {
        return this.$store.state[APP_CONFIG.stateKey.commonStore].customerLevels;
    }

    get customerLevelsDiscount() {
        return this.customerLevels.find((cl) => cl.ID === this.customerInfo.LevelID);
    }

    get userInfo() {
        return this.$store.state[APP_CONFIG.stateKey.authStore].userInfo;
    }

    get rate() {
      return this.$store.state[APP_CONFIG.stateKey.commonStore].rate;
    }

    @Watch('orderId', { immediate: true, deep: true })
    private async watchOrderId() {
        if (this.orderId) {
            this.initComponent();
        }
    }

    private async initComponent() {
        this.order.products = [];
        this.order.changedHistories = [];
        this.order.paymentHistory = [];
        this.order.orderComplains = [];
        await this.getCustomersInfo();
        if(!this.orderId) {
            return this.$router.push({ name: 'order-index' });
        }

        if (!this.customerLevels) {
            await this.getCustomerLevels();
        }

        await this.loadData();
    }

    private getStatus(value: any) {
        return DATA.orderStatus.find(x => x.value === value) || { value: 0, name: 'N/A', color: '#49494a' };
    }

    private async loadData() {
        const result = await orderService.orderById(this.orderId);
        if (result && result.data && result.data.status === 200) {
            const item = result.data.data;
            this.order = new Order();
            this.order.id = item.ID;
            this.order.amountCNY = item.AmountCNY;
            this.order.amountVND = item.AmountVND;
            this.order.buyerID = item.BuyerID;
            this.order.createdBy = item.CreatedBy;
            this.order.createdDate = item.CreatedDate;
            this.order.deposit = item.Deposit;
            this.order.depositDate = item.DepositDate;
            this.order.exchangeRate = item.ExchangeRate;
            this.order.feeBuy = item.FeeBuy;
            this.order.feeBuyAmount = item.FeeBuyAmount;
            this.order.feeBuyDiscount = item.FeeBuyDiscount;
            this.order.feeOther = item.FeeOther;
            this.order.feeOtherNote = item.FeeOtherNote;
            this.order.feeShipCN = item.FeeShipCN;
            this.order.feeShipCNVND = item.FeeShipCNVND;
            this.order.feeWeight = item.FeeWeight;
            this.order.feeWeightAmount = item.FeeWeightAmount;
            this.order.feeWeightDiscount = item.FeeWeightDiscount;
            this.order.isFragileGoods = item.IsFragileGoods;
            this.order.isCheck = item.IsCheck;
            this.order.isFast = item.IsFast;
            this.order.modifiedBy = item.ModifiedBy;
            this.order.modifiedDate = item.ModifiedDate;
            this.order.note = item.Note;
            this.order.feeShipType = item.FeeShipType;
            this.order.orderWeight = item.OrderWeight;
            this.order.receivePlaceID = item.ReceivePlaceID;
            this.order.chinaStoreID = item.ChinaStoreID;
            this.order.salerId = item.SalerID;
            this.order.shopId = item.ShopID;
            this.order.shopName = item.ShopName;
            this.order.site = item.Site;
            this.order.status = item.Status;
            this.order.statusDetail = this.getStatus(item.Status);
            this.order.fragileFee = item.FragileFee;
            this.order.fragileFeeVND = item.FragileFeeVND;
            this.order.checkFeeType = item.CheckFeeType;
            this.order.checkFee = item.CheckFee;
            this.order.checkFeeVND = item.CheckFeeVND;
            this.order.fastFee = item.FastFee;
            this.order.fastFeeVND = item.FastFeeVND;
            this.order.guaranteeAmount = item.GuaranteeAmount;
            this.order.guaranteeAmountVND = item.GuaranteeAmountVND;
            this.order.minDeposit = item.MinDeposit;
            this.order.totalAmountVND = item.TotalAmountVND;
            this.order.totalRealAmount = item.TotalRealAmount;
            this.order.totalRealAmountCNY = item.TotalRealAmountCNY;
            this.order.discountVND = item.DiscountVND;
            this.order.surcharge = item.Surcharge;
            this.order.uId = item.UID;

            this.order.products = [];
            for (const prod of item.Products) {
                const product = new Product();
                product.id = prod.ID;
                product.imageOrigin = prod.image_origin;
                product.linkOrigin = prod.link_origin;
                product.titleOrigin = prod.title_origin;
                product.priceOrigin = prod.price_origin;
                product.pricePromotion = prod.price_promotion;
                product.priceOriginVND = product.priceOrigin * item.ExchangeRate;
                product.pricePromotionVND = product.pricePromotion * item.ExchangeRate;
                product.amount = ((prod.price_promotion || prod.price_origin) * (prod.quantity || 1)  * item.ExchangeRate);
                product.amountCN = (prod.price_promotion || prod.price_origin) * (prod.quantity || 1);
                product.property = prod.property;
                product.quantity = prod.quantity;
                product.comment = prod.comment;
                this.order.products.push(product);
            }

            this.order.changedHistories = [];
            for(const hist of item.ChangedHistories) {
                const histItem = new ChangedHistoryModel();
                histItem.id = hist.ID;
                histItem.orderId = hist.OrderID;
                histItem.uId = hist.UID;
                histItem.userName = hist.Username;
                histItem.historyContent = hist.HistoryContent;
                histItem.orderType = hist.OrderType;
                histItem.type = hist.Type;
                histItem.createDate = moment(hist.CreatedDate).format('DD-MM-YYYY HH:mm:ss');
                this.order.changedHistories.push(histItem);
            }

            this.order.paymentHistory = [];
            for(const pay of item.PaymentHistory) {
                const payItem = new PaymentHistoryModel();
                payItem.amount = pay.Amount;
                payItem.createBy = pay.CreateBy;
                payItem.createDate = moment(pay.CreatedDate).format('DD-MM-YYYY HH:mm:ss');
                payItem.id = pay.ID;
                payItem.modifiedBy = pay.ModifiedBy;
                payItem.modifiedDate = pay.ModifiedDate;
                payItem.orderId = pay.OrderID;
                payItem.orderType = pay.OrderType;
                payItem.status = pay.Status;
                payItem.statusDetail = (DATA.paymentStatusList.find(x => x.value === pay.Status) || { name: 'Khác' }).name;
                payItem.type =  pay.Status === 12 ? 2 : 1;
                payItem.typeName = (DATA.paymentTypeList.find(x => x.value === pay.Type) || { name: 'Khác' }).name;
                this.order.paymentHistory.push(payItem);
            }

            this.order.orderComplains = [];
            for(const complain of item.OrderComplains) {
                const complainItem = new OrderComplainModel();
                complainItem.amount = complain.Amount;
                complainItem.amountNDT = complain.AmountNDT;
                complainItem.isOption = complain.IsOption;
                complainItem.modifiedBy = complain.ModifiedBy;
                complainItem.modifiedDate = complain.ModifiedDate ? moment(complain.ModifiedDate).format('DD-MM-YYYY HH:mm:ss') : '';
                complainItem.noteForUser = complain.NoteForUser;
                complainItem.noteOfStaff = complain.NoteOfStaff;
                complainItem.orderId = complain.OrderID;
                complainItem.orderType = complain.OrderType;
                complainItem.receivedId = complain.ReceivedID;
                complainItem.status = complain.Status;
                complainItem.statusObj = (DATA.complainStatusList.find(x => x.value === complain.Status) || { value: 0, name: 'Khác', color: '#3b4354' });
                complainItem.uId = complain.UID;
                complainItem.complainText = complain.ComplainText;
                complainItem.userName = complain.Username;
                complainItem.createBy = complain.CreatedBy;
                complainItem.createDate = moment(complain.CreatedDate).format('DD-MM-YYYY HH:mm:ss');
                complainItem.exchangeRate = complain.ExchangeRate;
                complainItem.id = complain.ID;
                complainItem.img = complain.IMG;
                complainItem.isBroken = complain.IsBroken;
                complainItem.isMissGood = complain.IsMissGood;
                this.order.orderComplains.push(complainItem);
            }
        }
    }

    private async onItemChange(dataEvent: any) {
      const dataRequest = {
        ID: dataEvent.id,
        quantity: (Number)(dataEvent.quantity),
        comment: dataEvent.comment
      };

      const result: any = await orderService.updateProduct(dataEvent.id, dataRequest);
      if (result.data.status === 200 && result.data.message === 'Success') {
        Vue.notify({ group: 'foo', type: 'success', title: 'Thông báo!', text: 'Cập nhật đơn hàng thành công!' });
        await this.loadData();
      }
    }

    private async onDeleteItem(dataEvent: any) {
        if(this.order.products.length === 1) {
            Vue.notify({ group: 'foo', type: 'error', title: 'Thông báo!', text: 'Đơn hàng phải tồn tại ít nhất 1 sản phẩm!' });
            return;
        }
        const result: any = await orderService.removeProduct(dataEvent.id);
        if (result.data.status === 200 && result.data.message === 'Success') {
            Vue.notify({ group: 'foo', type: 'success', title: 'Thông báo!', text: 'Cập nhật đơn hàng thành công!' });
            await this.loadData();
        }
    }

    private async onAddProduct(dataEvent: any) {
        const dataRequest = {
            OrderID: this.order.id,
            UID: this.order.uId,
            link_origin: dataEvent.link_origin,
            image_origin: dataEvent.image_origin,
            property: dataEvent.property,
            price_origin: dataEvent.price_origin,
            price_promotion: dataEvent.price_origin,
            quantity: (Number)(dataEvent.quantity),
            comment: dataEvent.comment
        };

        const result: any = await orderService.addProduct(dataRequest);
        if (result.data.status === 200 && result.data.message === 'Success') {
            Vue.notify({ group: 'foo', type: 'success', title: 'Thông báo!', text: 'Cập nhật đơn hàng thành công!' });
            await this.loadData();
        }
    }

    private onCancelOrder(data: any) {
        this.order = {
            ...this.order,
            status: 1,
            statusDetail: this.getStatus(1)
        }
    }

    private onReOrder(data: any) {
        this.order = {
            ...this.order,
            status: 3,
            statusDetail: this.getStatus(3)
        }
    }

    private onDepositOrder(data: any) {
        this.order = {
            ...this.order,
            status: 2,
            statusDetail: this.getStatus(2),
            deposit: this.order.minDeposit
        }
    }

    private async getCustomerLevels() {
        const result = await commonService.getCustomerLevels();
        if (result && result.status === 200) {
            (window['getApp'] as any).$emit('SET_CUSTOMER_LEVELS', result.data.data);
        }
    }

    private async getCustomersInfo() {
        const result = await authService.customersInfo(this.userInfo.userId);
        if(result && result.status === 200) {
            this.customerInfo = result.data.data || {};
            (window['getApp'] as any).$emit('APP_SET_CUSTOMER_INFO', result.data.data || {});
        }
    }
}
